import { Button } from "@cmsgov/ds-medicare-gov";
import React, { FunctionComponent, useContext, useState } from "react";
import { FormattedMessage } from "react-intl-phraseapp";
import { MailingAddress, SearchResultPlan } from "../../../../@types";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
  Ga4Event,
  Ga4EventType,
} from "../../../../app/contexts/Analytics/types";
import { AppContext } from "../../../../app/store";
import { ChangeLocationModal } from "../../../../components";
import { useTranslate } from "../../../../helpers";
import BasePlanCard from "./BasePlanCard";

interface FindPlansCardProps {
  county: string | undefined;
  state: string | undefined;
  zipcode: string | undefined;
  mailingAddress: MailingAddress;
  onFindPlansClick: () => void;
  currentCoverage?: SearchResultPlan;
}

const FindPlansCard: FunctionComponent<FindPlansCardProps> = ({
  county,
  mailingAddress,
  onFindPlansClick,
  state,
  zipcode,
  currentCoverage,
  ...props
}) => {
  const t = useTranslate();
  const [showChangeLocationModal, setShowChangeLocationModal] = useState(false);
  const { dispatch } = useContext(AppContext);
  const toggleChangeLocationModal = () => {
    setShowChangeLocationModal(modalStatus => !modalStatus);
  };

  const hasCompleteMailingAddress =
    !!mailingAddress.city && !!mailingAddress.state && !!mailingAddress.zipcode;
  const changeLocationModalHeader = t(
    "pharmacy_selection.change_location.title"
  );

  return (
    <>
      <BasePlanCard
        dataTestId="find-plans-card"
        header={
          currentCoverage
            ? t("summary_page.plan_cards.find_plans.header")
            : t("summary_page.plan_cards.find_plans.no_current_coverage.header")
        }
        planHeader={
          <>
            <p className="mct-c-summary-page-plan-card__plan-header">
              {county && state && zipcode ? (
                <FormattedMessage
                  id="summary_page.plan_cards.find_plans.plan_header"
                  values={{ county, state, zipcode }}
                />
              ) : hasCompleteMailingAddress ? (
                <FormattedMessage
                  id="summary_page.plan_cards.find_plans.plan_header"
                  values={{
                    county: mailingAddress.city,
                    state: mailingAddress.state,
                    zipcode: mailingAddress.zipcode,
                  }}
                />
              ) : (
                t("summary_page.plan_cards.find_plans.plan_header.no_location")
              )}
            </p>
            <p className="mct-c-summary-page-plan-card__plan-name mct-hide-print">
              (
              <Button
                variation="ghost"
                className="ds-u-padding--0 ds-u-font-size--lg"
                onClick={() => {
                  dispatch({
                    type: AnalyticsActionType.SEND_GA4_EVENT,
                    settings: {
                      event_name: Ga4Event.MODAL_IMPRESSION,
                      heading: changeLocationModalHeader,
                    },
                  });
                  dispatch({
                    type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                    settings: {
                      event_action: "modal impression",
                      event_label: changeLocationModalHeader,
                      other_props: {
                        event_category: Ga4EventType.UI_INTERACTION,
                      },
                    },
                  });
                  dispatch({
                    type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                    settings: {
                      button: {
                        buttonStyle: AnalyticsButtonStyle.TRANSPARENT,
                        buttonType: AnalyticsButtonType.BUTTON,
                        text: t("summary_page.links.change_location"),
                      },
                    },
                  });
                  toggleChangeLocationModal();
                }}
              >
                {t("summary_page.links.change_location")}
              </Button>
              )
            </p>
          </>
        }
        footer={
          <Button
            variation="solid"
            // @TODO, look at what this mysteerm classname was supposed to be
            className="e2e-find-plans-button mds-c-button--mobile-full-width"
            onClick={() => {
              dispatch({
                type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                settings: {
                  button: {
                    buttonStyle: AnalyticsButtonStyle.PRIMARY,
                    buttonType: AnalyticsButtonType.BUTTON,
                    text: t("summary_page.links.find_plans_now"),
                  },
                },
              });
              onFindPlansClick();
            }}
          >
            {t("summary_page.links.find_plans_now")}
          </Button>
        }
        {...props}
      />
      {showChangeLocationModal && (
        <ChangeLocationModal
          onModalClose={() => {
            dispatch({
              type: AnalyticsActionType.SEND_GA4_EVENT,
              settings: {
                event_name: Ga4Event.MODAL_CLOSED,
                heading: changeLocationModalHeader,
              },
            });
            dispatch({
              type: AnalyticsActionType.SEND_TEALIUM_EVENT,
              settings: {
                event_action: "closed modal",
                event_label: changeLocationModalHeader,
                other_props: {
                  event_category: Ga4EventType.UI_INTERACTION,
                },
              },
            });
            toggleChangeLocationModal();
          }}
          data-testid="ChangeLocationModal"
        />
      )}
    </>
  );
};

export default FindPlansCard;
